import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/cms/pages/list/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import TextEditor from "components/wysiwyg/Wysiwyg";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import cn from "clsx";
import {
  type MainTextExtended,
  handleUpdateDraftItem as onUpdateDraftItem,
} from "store/cms/footer/mainTexts/slice";
import styles from "./TextMainTab.module.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getGeoDropdownOptions } from "helpers/geoDropdown";

type Props = {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors?: () => void;
  readOnly?: boolean;
};

const TextMainTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  resetErrors,
  readOnly
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const geoList = useAppSelector(selectFilteredGeo);
  const draftItem = useAppSelector(
    (state) => state["cms-main-texts"].draftItem,
  );

  useEffect(() => {
    resetErrors?.();
  }, [draftItem.geo]);

  const handleChangeData = (value: Partial<MainTextExtended>) => {
    dispatch(onUpdateDraftItem(value));
  };

  return (
    <div
      className={cn(
        styles.textMainTab,
        "bonus-form-content pages-form-content",
      )}
    >
      <div className={cn(styles.textTabUpper, "row")}>
        <div className={cn(styles.textTabUpperItem, "bonus-form-box--item")}>
          <InnerInput
            value={draftItem?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="text-main-title"
            onChange={(e) => {
              handleChangeData({ title: e });
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>
        <div className={cn(styles.textTabUpperItem, "bonus-form-box--item")}>
          <DefaultDropdown
            options={getGeoDropdownOptions(geoList)}
            label={t("geo")}
            placeholder="GEO"
            value={draftItem?.geo || geoList[0]}
            onSelect={(t) => {
              handleChangeData({ geo: `${t}` });
              clearFieldError("geo");
            }}
            hasError={Boolean(fieldErrors?.["geo"])}
            disabled={readOnly}
          />
        </div>
        <div className={cn(styles.textTabUpperItem, "bonus-form-box--item")}>
          <SwitchBtn
            activeTxt={t("active")}
            isActive={draftItem?.isActive || false}
            handleClick={() =>
              handleChangeData({ isActive: !draftItem?.isActive })
            }
            disabled={readOnly}
          />
        </div>
      </div>
      <div
        className={cn("bonus-form-box--item html-editor", styles.textTabEditor)}
      >
        <div className="label">{t("content")}</div>
        <TextEditor
          value={draftItem?.content ?? ""}
          onChange={(html) => {
            handleChangeData({ content: html });
            clearFieldError?.("content");
          }}
          hasError={Boolean(fieldErrors?.["content"])}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default TextMainTab;
